import "moment-timezone";
import moment from "moment";
import Field from "./field";
import Documents from "./documents";
import Api from "./api";
import { normalize, replaceAll } from "../utils";
import IPeriodValue from "../interface/period-value";
import IConfigDatetime from "../interface/field-cfg-datetime";
import IField from "../interface/field";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import IApiField from "../interface/api/api-field";
import Global from "../global";
import FieldType from "../enum/field-type";
import DateType from "../enum/date-type";

class DocumentValueInstance {

    /* Public Methods */

    public async getByPath(doc: IDocument, pathId: number[]): Promise<IDocumentValue> {
        const path: number[] = [...pathId];
        while (path.length > 1) {
            const fieldId: number | undefined = path.shift();
            if (fieldId) {
                if (doc[`c${fieldId}`] instanceof Array) {
                    if (!doc[`d${fieldId}`]) {
                        const field: IApiField | null = Api.getField(fieldId);
                        if (field && field.pointer) {
                            const id: number = (doc[`c${fieldId}`] as number[])[0] || 0;
                            if (id) {
                                const related: IDocument | null = await Documents.loadOne(field.pointer[1].ptEcn, id);
                                if (related) {
                                    doc[`d${fieldId}`] = [related];
                                }
                            }
                        }
                    }
                    if (doc[`d${fieldId}`]) {
                        if (doc[`d${fieldId}`] instanceof Array) {
                            doc = (doc[`d${fieldId}`] as IDocument[])[0] || {};
                        } else {
                            // TODO - Backbone delete - now used for masterdetail, email fails because doc[dXXX] is not an array but document (there is another one, search begin of comment)
                            // Leave only doc[`d${fieldId}`][0]!!!
                            doc = doc[`d${fieldId}`] as IDocument;
                        }
                    } else {
                        doc = {};
                    }
                }
            }
        }
        if (doc) {
            if (typeof doc[`c${path[0]}`] !== 'undefined') {
                return doc[`c${path[0]}`];
            }
        }
        return '';
    }

    public getByPathSync(doc: IDocument, pathId: number[], field: IField | null = null): IDocumentValue {
        if (pathId.length === 1) {
            if (!field || field.id !== pathId[0]) {
                field = Field.get(pathId[0]);
            }
            if (field) {
                if (field.uiType === FieldType.linked) {
                    return doc[`c${field.linkedRelation[0]}`];
                }
                return doc[`${DocumentValue.getPrefix(field)}${pathId}`];
            }
            return doc[`c${pathId}`];
        } else {
            const value: IDocument[] = DocumentValue.getPointer(doc[`d${pathId[0]}`]);
            if (value.length) {
                return this.getByPathSync(value[0], pathId.slice(1, pathId.length));
            }
        }
        return null;
    }

    public getString(value: IDocumentValue): string {
        let str: string = '';
        if (typeof value === 'string') {
            str = value as string;
        }
        return replaceAll(normalize(str).toLowerCase(), '\n', ' ');
    }

    public getNumber(value: IDocumentValue): number | null {
        return typeof value === 'number' ? value : null;
    }

    public getDatetime(value: IDocumentValue, field: IField): number | null {
        const config: IConfigDatetime = Field.getDatetimeConfig(field);
        if (typeof value === 'string' && value) {
            let mom: moment.Moment;
            if (config.type === DateType.DateOnly) {
                mom = moment.utc((value as string).split('T')[0], moment.ISO_8601);
            } else {
                mom = moment(value as string, moment.ISO_8601);
            }
            if (mom.isValid()) {
                // TODO - Exclude .unix??? This is used in date-filter-value
                return mom.unix();
            }
        }
        return null;
    }

    public getLinked(value: IDocumentValue): number[] {
        if (value instanceof Array && typeof value.length) {
            return value as number[];
        }
        return [];
    }

    public getLinkedId(value: IDocumentValue): number | null {
        if (value instanceof Array && typeof value.length) {
            return (value as number[])[0];
        }
        return null;
    }

    public getPointer(value: IDocumentValue): IDocument[] {
        if (value instanceof Array && typeof value.length) {
            return value as IDocument[];
        }
        return [];
    }

    public getPointerId(doc: IDocument, field: IField): number | null {
        let fieldId: number = field.linkedRelation[0];
        let value: IDocumentValue = doc[`c${fieldId}`];
        if (value instanceof Array && typeof value[0] === 'number') {
            return value[0];
        }
        return null;
    }

    public getPeriod(value: IDocumentValue): IPeriodValue | null {
        if (typeof value === 'object') {
            return value as IPeriodValue;
        }
        return null;
    }

    public getPrefix(field: IField): string {
        switch(field.uiType) {
            case FieldType.linked:
            case FieldType.lookup:
                return 'd';
            case FieldType.formula:
                return Global.FORMULA_PROP_PREFIX;
            default:
                return 'c';
        }
    }

}

const DocumentValue = new DocumentValueInstance();

export default DocumentValue;